// Story (storygen R2) query + mutation hooks.
//
// Mirrors useScript.ts: stable query-key factories + a create mutation that
// seeds the per-story cache and invalidates the list so a fresh story shows
// without an extra round-trip.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { CreateStoryInput, ShootingPlan, Story, UpdateStoryInput } from '@/api/types'

/** Stable cache key for the recent-stories list. */
export const storiesKey = ['stories'] as const

/** Stable cache key factory for a single story. */
export const storyKey = (id: number) => ['story', id] as const

/**
 * useStories — fetch the 50 most-recent stories (updated_at DESC). Generation is
 * async (the chain runs in the background), so poll every 2s while any story is
 * still `generating`, then idle.
 */
export function useStories() {
  return useQuery<Story[]>({
    queryKey: storiesKey,
    queryFn: api.listStories,
    refetchInterval: (query) =>
      query.state.data?.some((s) => s.status === 'generating') ? 2000 : false,
  })
}

/**
 * useStory — fetch a single story by id. `enabled` gates the query while no
 * story is selected (the route passes `enabled={id > 0}`).
 */
export function useStory(id: number, enabled = true) {
  return useQuery<Story>({
    queryKey: storyKey(id),
    queryFn: () => api.getStory(id),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.status === 'generating' ? 2000 : false,
  })
}

/**
 * useCreateStory — POST a concept and persist the returned story straight into
 * its own cache key, then invalidate the list so the new row appears.
 */
export function useCreateStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateStoryInput) => api.createStory(input),
    onSuccess: (data) => {
      qc.setQueryData<Story>(storyKey(data.id), data)
      qc.invalidateQueries({ queryKey: storiesKey })
    },
  })
}

/** Shared cache writer for the in-place mutations (retry/update). */
function writeStory(qc: ReturnType<typeof useQueryClient>, data: Story) {
  qc.setQueryData<Story>(storyKey(data.id), data)
  qc.invalidateQueries({ queryKey: storiesKey })
}

/** useUpdateStory — PATCH a story's concept (resets it to draft). */
export function useUpdateStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateStoryInput }) =>
      api.updateStory(id, input),
    onSuccess: (data) => writeStory(qc, data),
  })
}

/** useUpdateStoryPlan — save operator scene/character edits to the shooting_plan. */
export function useUpdateStoryPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, plan }: { id: number; plan: ShootingPlan }) =>
      api.updateStoryPlan(id, plan),
    onSuccess: (data) => writeStory(qc, data),
  })
}

/** useRetryStory — regenerate a story in place (synchronous; returns the row). */
export function useRetryStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.retryStory(id),
    onSuccess: (data) => writeStory(qc, data),
  })
}

/** useDeleteStory — remove a story, then drop it from the cache + list. */
export function useDeleteStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteStory(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: storyKey(id) })
      qc.invalidateQueries({ queryKey: storiesKey })
    },
  })
}
